import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

export default function DocumentDetailPanel({ docId, onClose, user, onUpdated }) {
  const { t } = useTranslation();
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [newNotes, setNewNotes] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (docId) loadDetail();
  }, [docId]);

  const loadDetail = async () => {
    try {
      const res = await axios.get(`/cycle3/documents/${docId}`);
      setDetail(res.data);
      setNewNotes(res.data?.document?.notes || '');
    } catch (e) {
      toast.error(t("documents.failedToLoadDocument"));
    }
  };

  const doc = detail?.document;
  const versions = useMemo(() => detail?.versions || [], [detail?.versions]);

  const canEditNotes = ['owner', 'management', 'engineer'].includes(user.role);
  const canSupersede = ['owner', 'management'].includes(user.role);
  const canReplace = canEditNotes;

  const isPreviewable = (mime) => {
    if (!mime) return false;
    return mime.includes('pdf') || mime.includes('image');
  };

  const isImage = (mime) => mime && mime.includes('image');

  const [thumbs, setThumbs] = useState([]);
  useEffect(() => {
    if (!versions) return;
    const imgs = versions.filter(v => isImage(v.mime_type)).slice(0, 12).map(v => ({ id: v.id, src: `/api/cycle3/documents/${v.id}/download`, alt: v.original_filename }));
    setThumbs(imgs);
  }, [versions]);

  const doReplace = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error(t("documents.fileTooLarge"));
      return;
    }
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    if (!allowed.includes(file.type)) {
      toast.error(t("documents.unsupportedFileType"));
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('project_id', doc.project_id);
      fd.append('doc_type', doc.doc_type);
      if (doc.entity_type) fd.append('entity_type', doc.entity_type);
      if (doc.entity_id) fd.append('entity_id', doc.entity_id);
      await axios.post('/cycle3/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(t("documents.newVersionUploaded"));
      onUpdated && onUpdated();
      loadDetail();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || t("documents.failedToUpload"));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const saveNotes = async () => {
    try {
      await axios.patch(`/cycle3/documents/${doc.id}/meta`, { notes: newNotes });
      toast.success(t("documents.notesUpdated"));
      setEditing(false);
      loadDetail();
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("documents.failedToUpdate"));
    }
  };

  const supersede = async () => {
    try {
      await axios.patch(`/cycle3/documents/${doc.id}/supersede`);
      toast.success(t("documents.markedSuperseded"));
      loadDetail();
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("documents.failedToSupersede"));
    }
  };

  const [selectedVersionId, setSelectedVersionId] = useState(null);
  useEffect(() => { if (doc) setSelectedVersionId(doc.id); }, [docId, doc?.id]);

  const selected = useMemo(() => versions.find(v => v.id === selectedVersionId) || doc, [selectedVersionId, versions, doc]);

  const copyPath = async () => {
    try { await navigator.clipboard.writeText(selected.file_path); toast.success(t("documents.pathCopied")); } catch {}
  };

  if (!docId || !selected) return null;

  return (
    <div className="w-[420px] border-l bg-white/70 backdrop-blur-sm h-screen sticky top-0 p-4 overflow-y-auto">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm text-[#94A3B8]">{t("documents.document")}</div>
          <div className="font-medium text-[#0F172A] break-all">{selected.original_filename}</div>
          <div className="text-xs text-[#94A3B8]">v{selected.version || 1} • {selected.doc_type} • {selected.entity_type || 'project'}</div>
        </div>
        <Button variant="outline" onClick={onClose}>{t("documents.close")}</Button>
      </div>

      {/* Thumbnails for images */}
      {thumbs.length > 0 && (
        <div className="grid grid-cols-6 gap-2 mb-3">
          {thumbs.map(th => (
            <img key={th.id} src={th.src} alt={th.alt} className={`w-full h-16 object-cover rounded cursor-pointer ${selectedVersionId === th.id ? 'ring-2 ring-blue-500' : ''}`} onClick={() => setSelectedVersionId(th.id)} />
          ))}
        </div>
      )}

      {/* Preview */}
      <Card className="border-white/60 mb-3">
        <CardContent className="p-3">
          {isPreviewable(selected.mime_type) ? (
            selected.mime_type.includes('image') ? (
              <img src={`/api/cycle3/documents/${selected.id}/download`} alt={selected.original_filename} className="max-w-full rounded" />
            ) : (
              <iframe title="preview" src={`/api/cycle3/documents/${selected.id}/download`} className="w-full h-[280px] border rounded" />
            )
          ) : (
            <Button onClick={() => window.open(`/api/cycle3/documents/${selected.id}/download`, '_blank')}>{t("documents.downloadDocument")}</Button>
          )}
        </CardContent>
      </Card>

      {/* Meta */}
      <div className="space-y-1 text-sm text-[#334155]">
        <div><span className="text-[#94A3B8]">{t("documents.entity")}:</span> {selected.entity_type || 'project'} • {selected.entity_id || '-'}</div>
        <div><span className="text-[#94A3B8]">{t("documents.uploader")}:</span> {selected.uploaded_by?.slice(0, 8) || '-'}</div>
        <div><span className="text-[#94A3B8]">{t("documents.uploaded")}:</span> {new Date(selected.uploaded_at).toLocaleString()}</div>
        <div><span className="text-[#94A3B8]">{t("documents.hash")}:</span> {selected.file_hash || '-'}</div>
        <div className="flex items-center gap-2"><span className="text-[#94A3B8]">{t("documents.storage")}:</span> <span className="truncate">{selected.file_path}</span> <Button size="sm" variant="outline" onClick={copyPath}>{t("documents.copy")}</Button></div>
      </div>

      {/* Notes */}
      <div className="mt-3">
        <Label>{t("documents.notes")}</Label>
        {canEditNotes ? (
          editing ? (
            <>
              <Textarea rows={3} value={newNotes} onChange={(e) => setNewNotes(e.target.value)} />
              <div className="flex gap-2 mt-2">
                <Button size="sm" onClick={saveNotes}>{t("documents.save")}</Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)}>{t("documents.cancel")}</Button>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-between">
              <div className="text-sm text-[#334155] whitespace-pre-wrap">{selected.notes || '-'}</div>
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>{t("documents.edit")}</Button>
            </div>
          )
        ) : (
          <div className="text-sm text-[#334155]">{selected.notes || '-'}</div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-3 flex gap-2">
        {canReplace && (
          <>
            <input type="file" id="replace-file" className="hidden" onChange={doReplace} />
            <Button size="sm" onClick={() => document.getElementById('replace-file').click()} disabled={uploading}>{t("documents.replaceWithNewVersion")}</Button>
          </>
        )}
        {canSupersede && <Button size="sm" variant="outline" onClick={supersede}>{t("documents.markSuperseded")}</Button>}
      </div>

      {/* Versions */}
      <div className="mt-4">
        <div className="text-sm font-medium mb-2">{t("documents.versionHistory")}</div>
        <div className="space-y-2">
          {versions.map(v => (
            <div key={v.id} className={`p-2 border rounded cursor-pointer ${v.superseded ? 'bg-white/50' : 'bg-white/70 backdrop-blur-sm'} ${selectedVersionId === v.id ? 'ring-1 ring-blue-400' : ''}`} onClick={() => setSelectedVersionId(v.id)}>
              <div className="flex items-center justify-between">
                <div className="text-sm">v{v.version || 1} • {new Date(v.uploaded_at).toLocaleString()}</div>
                <div className="text-xs text-[#94A3B8]">{v.superseded ? t("documents.superseded") : t("documents.current")}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
